import React, { useState } from "react";
import { getData, getUserToken } from "@/utils/localStorage.tsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, ApiResponse } from "@/types/Api.tsx";
import { BankAccount, BankFile } from "@/types/BankSettings.tsx";
import {
  BankAccountQueryKey,
  BankFileQueryKey,
  BusinessInfoQueryKey,
  CoPQueryKey,
  NaturalPersonQueryKey,
} from "@/config/constants/QueryKeys.tsx";
import GenericApi from "@/api/genericApi.tsx";
import { ApiRoutes } from "@/config/routes/ApiRoutes.tsx";
import { ValidationErrors } from "@/types/ValidationError.tsx";
import axios from "axios";
import CustomLogger from "@/utils/CustomLogger.tsx";
import { AuthResponse } from "@/types/Auth.tsx";
import { authStorageUser } from "@/config/constants/StorageKeys.tsx";
import { User } from "@/types/User.tsx";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { useSnackBarAlert } from "@/hooks/useSnackbar.tsx";
import { Loader } from "@/components";
import { BusinessInfoNEW, NaturalPersonNEW } from "@/types/BusinessInfoNEW";
import { isBitramp, validatePdfFile } from "@/utils/utils";
import genericApi from "@/api/genericApi.tsx";

const Cloude: React.FC = () => {
  const baseUrl: string = GenericApi.baseUrl;
  const queryClient = useQueryClient();
  const { selectedClient } = useSelector((state: RootState) => state.client);

  const userData = getData<AuthResponse>(authStorageUser)?.user;
  const [user, setUser] = React.useState<User | null>(null);
  const isDisabled = user?.is_staff;

  const [incorporation_certificate, setIncorporationCertificate] =
    useState<File | null>(null);
  const [good_standing_certificate, setGoodStandingCertificate] =
    useState<File | null>(null);
  const [shareholders_certificate, setShareholdersCertificate] =
    useState<File | null>(null);
  const [directors_certificate, setDirectorsCertificate] =
    useState<File | null>(null);
  const [notice_of_registered_office, setNoticeOfRegisteredOffice] =
    useState<File | null>(null);
  const [id_of_person_managing_company, setIdOfPersonManagingCompany] =
    useState<File | null>(null);
  const [
    resolution_who_is_authorised_on_behalf,
    setResolutionWhoIsAuthorisedOnBehalf,
  ] = useState<File | null>(null);
  const [id_of_who_is_authorised_on_behalf, setIdOfWhoIsAuthorisedOnBehalf] =
    useState<File | null>(null);
  const [poa, setPOA] = useState<File | null>(null);
  const [poi, setPOI] = useState<File | null>(null);
  const [passid, setPASSID] = useState<File | null>(null);

  const { data: userIndividual } = useQuery<ApiResponse<NaturalPersonNEW>>({
    queryFn: async () =>
      GenericApi.get<NaturalPersonNEW>(
        `${
          selectedClient
            ? `${ApiRoutes.INDIVIDUALINFO}?user_id=${selectedClient.id}`
            : `${ApiRoutes.INDIVIDUALINFO}`
        }`
      ),
    queryKey: [NaturalPersonQueryKey],
  });

  const { data: userBusinessInfo } = useQuery<ApiResponse<BusinessInfoNEW>>({
    queryFn: async () =>
      GenericApi.get<BusinessInfoNEW>(
        `${
          selectedClient
            ? `${ApiRoutes.BUSINESSINFO}?user_id=${selectedClient.id}`
            : `${ApiRoutes.BUSINESSINFO}`
        }`
      ),
    queryKey: [BusinessInfoQueryKey],
  });

  const addBusinessInfoFiles = useMutation<
    ApiResponse<unknown>,
    ApiError<ValidationErrors>,
    {
      fileName: string;
      file: File | null;
    }
  >({
    mutationFn: async ({ fileName, file }): Promise<ApiResponse<unknown>> => {
      const formData = new FormData();

      if (file === null) {
        showSnackBar(
          "File is required",
          "error",
          {
            vertical: "top",
            horizontal: "right",
          },
          5000
        );
        return {
          data: null,
          error: {
            name: "Error",
            message: "File is required",
            stack: "",
          } as Error,
        };
      }

      formData.append(`${fileName}`, file!);

      const apiResponse: ApiResponse<unknown> = {
        data: null,
        error: null,
      };

      const res = await axios.post<ApiResponse<unknown>>(
        `${baseUrl}/user-info/business-files`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${getUserToken()}`,
          },
        }
      );

      if (res.status !== 200) {
        apiResponse.error = {
          name: "Error",
          message: "An error occurred",
          stack: "",
        } as Error;
        return apiResponse;
      }
      return apiResponse;
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({ queryKey: [BusinessInfoQueryKey] });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  const addIndividualFiles = useMutation<
    ApiResponse<unknown>,
    ApiError<ValidationErrors>,
    {
      fileName: string;
      file: File | null;
    }
  >({
    mutationFn: async ({ fileName, file }): Promise<ApiResponse<unknown>> => {
      const formData = new FormData();

      if (file === null) {
        showSnackBar(
          "File is required",
          "error",
          {
            vertical: "top",
            horizontal: "right",
          },
          5000
        );
        return {
          data: null,
          error: {
            name: "Error",
            message: "File is required",
            stack: "",
          } as Error,
        };
      }

      formData.append(`${fileName}`, file!);

      const apiResponse: ApiResponse<unknown> = {
        data: null,
        error: null,
      };

      const res = await axios.post<ApiResponse<unknown>>(
        `${baseUrl}/user-info/individual-files`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${getUserToken()}`,
          },
        }
      );

      if (res.status !== 200) {
        apiResponse.error = {
          name: "Error",
          message: "An error occurred",
          stack: "",
        } as Error;
        return apiResponse;
      }
      return apiResponse;
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({ queryKey: [NaturalPersonQueryKey] });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileName: string
  ) => {
    const isValidated = validatePdfFile(
      e.target.files ? e.target.files[0] : null
    );
    if (!isValidated) {
      return;
    }
    // setPOA(e.target.files ? e.target.files[0] : null);
    if (fileName === "certificate_of_incorporation") {
      setIncorporationCertificate(e.target.files ? e.target.files[0] : null);
      addBusinessInfoFiles.mutate({
        fileName: "incorporation_certificate",
        file: e.target.files ? e.target.files[0] : null,
      });
    }
    if (fileName === "certificate_of_good_standing") {
      setGoodStandingCertificate(e.target.files ? e.target.files[0] : null);
      addBusinessInfoFiles.mutate({
        fileName: "good_standing_certificate",
        file: e.target.files ? e.target.files[0] : null,
      });
    }
    if (fileName === "certificate_of_shareholders") {
      setShareholdersCertificate(e.target.files ? e.target.files[0] : null);
      addBusinessInfoFiles.mutate({
        fileName: "shareholders_certificate",
        file: e.target.files ? e.target.files[0] : null,
      });
    }
    if (fileName === "certificate_of_directors") {
      setDirectorsCertificate(e.target.files ? e.target.files[0] : null);
      addBusinessInfoFiles.mutate({
        fileName: "directors_certificate",
        file: e.target.files ? e.target.files[0] : null,
      });
    }
    if (fileName === "notice_of_registered_office") {
      setNoticeOfRegisteredOffice(e.target.files ? e.target.files[0] : null);
      addBusinessInfoFiles.mutate({
        fileName: "notice_of_registered_office",
        file: e.target.files ? e.target.files[0] : null,
      });
    }
    if (fileName === "identification_documents") {
      setIdOfPersonManagingCompany(e.target.files ? e.target.files[0] : null);
      addBusinessInfoFiles.mutate({
        fileName: "id_of_person_managing_company",
        file: e.target.files ? e.target.files[0] : null,
      });
    }
    if (fileName === "resolution_specifying") {
      setResolutionWhoIsAuthorisedOnBehalf(
        e.target.files ? e.target.files[0] : null
      );
      addBusinessInfoFiles.mutate({
        fileName: "resolution_who_is_authorised_on_behalf",
        file: e.target.files ? e.target.files[0] : null,
      });
    }
    if (fileName === "identification_documents_authorised") {
      setIdOfWhoIsAuthorisedOnBehalf(e.target.files ? e.target.files[0] : null);
      addBusinessInfoFiles.mutate({
        fileName: "id_of_who_is_authorised_on_behalf",
        file: e.target.files ? e.target.files[0] : null,
      });
    }
    if (fileName === "poa") {
      setPOA(e.target.files ? e.target.files[0] : null);
      addIndividualFiles.mutate({
        fileName: "proof_of_address",
        file: e.target.files ? e.target.files[0] : null,
      });
    }
    if (fileName === "poi") {
      setPOI(e.target.files ? e.target.files[0] : null);
      addIndividualFiles.mutate({
        fileName: "proof_of_income",
        file: e.target.files ? e.target.files[0] : null,
      });
    }
    if (fileName === "passid") {
      setPASSID(e.target.files ? e.target.files[0] : null);
      addIndividualFiles.mutate({
        fileName: "passport_file",
        file: e.target.files ? e.target.files[0] : null,
      });
    }
  };

  const handleOpenDocument = (link: string) => {
    window.open(link, "_blank");
  };

  const { isLoading } = useQuery<ApiResponse<User>>({
    queryFn: async () =>
      GenericApi.get<User>(
        `${ApiRoutes.USERS}/${
          selectedClient ? selectedClient.id : userData?.id
        }`,
        undefined,
        (data) => {
          setUser(data);
        }
      ),
    queryKey: [CoPQueryKey, selectedClient ? selectedClient.id : userData?.id],
  });

  const { data: bankAccounts, isLoading: bankAccountsLoading } = useQuery<
    ApiResponse<BankAccount[]>
  >({
    queryKey: [
      BankAccountQueryKey,
      selectedClient ? selectedClient.id : userData?.id,
    ],
    queryFn: async () =>
      GenericApi.get<BankAccount[]>(
        `${ApiRoutes.BANKACCOUNT}${
          selectedClient ? `/user/${selectedClient.id}` : ""
        }`
      ),
  });

  const validateFileTypes = (file: File) => {
    switch (file.type) {
      case "application/pdf":
        return true;
      case "image/jpeg":
        return true;
      case "image/png":
        return true;
      default:
        return false;
    }
  };

  const validateMaxFileSize = (file: File) => {
    const fileSizeInMB = file.size / 1024 / 1024;
    return fileSizeInMB <= 5;
  };

  const { showSnackBar } = useSnackBarAlert();

  const getFileName = (filePath: File | string | null) => {
    if (!filePath) return "";
    if (filePath instanceof File) {
      return filePath.name;
    }
    return filePath.split("/").pop();
  };

  const downloadFile = (file: string | null) => {
    if (!file) return;
    // const defaultFileName = "file.pdf";
    const defaultUrl = GenericApi.baseUrl.split("/api")[0];
    const url = `${defaultUrl}${file}`;
    window.open(url, "_blank");
    // const link = document.createElement("a");
    // link.href = url;
    // link.setAttribute("download", getFileName(file) ?? defaultFileName);
    // document.body.appendChild(link);
    // link.click();

    //* Td download directly from the server in case it's needed
    // fetch(url)
    //     .then((response) => {
    //       if (!response.ok) {
    //         throw new Error('Network response was not ok');
    //       }
    //       return response.blob(); // Get the file as a Blob
    //     })
    //     .then((blob) => {
    //       // Create a temporary link for download
    //       const link = document.createElement('a');
    //       const blobUrl = URL.createObjectURL(blob);
    //       link.href = blobUrl;
    //       link.setAttribute('download', getFileName(file) ?? defaultFileName);
    //       document.body.appendChild(link);
    //       link.click();
    //       URL.revokeObjectURL(blobUrl); // Clean up the object URL
    //       document.body.removeChild(link);
    //     })
    //     .catch((error) => {
    //       console.error('File download failed:', error);
    //     });
  };

  const addBankFile = useMutation<
    ApiResponse<BankFile>,
    ApiError<ValidationErrors>,
    { statement: File | null; id: number }
  >({
    mutationFn: async ({ statement, id }) => {
      const formData = new FormData();
      formData.append("statement", statement!);
      const response = await axios.post<ApiResponse<BankFile>>(
        `${genericApi.baseUrl}/bank-account/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${getUserToken()}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({ queryKey: [BankFileQueryKey] });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: [BankAccountQueryKey] });
    },
  });

  const handleUploadBankStatementFile = (
    bankId: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.files || e.target.files.length === 0) {
      showSnackBar(
        "File is required",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
      e.target.value = "";
      return;
    }

    if (!validateFileTypes(e.target.files[0])) {
      showSnackBar(
        "Invalid file type. Only PDF, JPEG, PNG files are allowed",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
      e.target.value = "";
      return;
    }

    if (!validateMaxFileSize(e.target.files[0])) {
      showSnackBar(
        "File size should not exceed 5MB",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
      e.target.value = "";
      return;
    }

    const bankStatementUpload = e.target.files[0];

    addBankFile.mutate({ statement: bankStatementUpload, id: bankId });
  };

  if (bankAccountsLoading || isLoading) return <Loader />;

  return (
    <div className="cloud min-h-[100vh] Text">
      <div className="cloud__title px-8 pt-8 pb-3 Heading2 dark-blue bold">
        Cloud
      </div>
      <div className="Container px-8 py-8 flex flex-col pshadow">
        <div className="">
          <div className="flex items-center w-full">
            <h2
              className={`w-fit whitespace-nowrap semi-bold ${
                isBitramp() ? "text-white" : ""
              }`}
            >
              Compliance, Anti-money Laundering and Counter Terrorist Financing
              Requirements
            </h2>
            {isBitramp() ? (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg" />
            ) : (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg" />
            )}
          </div>

          <div className="w-full ps-8">
            <div className="w-full">
              <div className="py-2 flex justify-between items-center w-full">
                <label className="block medium light-blue">
                  Certificate of Incorporation
                </label>
                <span
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data?.incorporation_certificate ?? ""
                    )
                  }
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                >
                  {getFileName(
                    (incorporation_certificate ??
                      userBusinessInfo?.data?.incorporation_certificate) ||
                      null
                  )}
                </span>
                {(incorporation_certificate ||
                  userBusinessInfo?.data?.incorporation_certificate) && (
                  <div className="mr-4"></div>
                )}
                <label
                  // className={
                  //   incorporation_certificate ||
                  //   userBusinessInfo?.data?.incorporation_certificate
                  //     ? "Outline_button"
                  //     : "Outline_button"
                  // }
                  className={
                    isBitramp()
                      ? incorporation_certificate ||
                        userBusinessInfo?.data?.incorporation_certificate
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : incorporation_certificate ||
                        userBusinessInfo?.data?.incorporation_certificate
                      ? "Outline_button"
                      : "Outline_button"
                  }
                >
                  Upload
                  <input
                    type="file"
                    name="certificate_of_incorporation"
                    onChange={(e) => {
                      handleFileUpload(e, "certificate_of_incorporation");
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="py-2 flex justify-between items-center w-full">
                <label className="block medium  light-blue">
                  Certificate of Good Standing
                </label>
                <span
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data?.good_standing_certificate ?? ""
                    )
                  }
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                >
                  {getFileName(
                    (good_standing_certificate ??
                      userBusinessInfo?.data?.good_standing_certificate) ||
                      null
                  )}
                </span>
                {(good_standing_certificate ||
                  userBusinessInfo?.data?.good_standing_certificate) && (
                  <div className="mr-4"></div>
                )}
                <label
                  className={
                    isBitramp()
                      ? good_standing_certificate ||
                        userBusinessInfo?.data?.good_standing_certificate
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : good_standing_certificate ||
                        userBusinessInfo?.data?.good_standing_certificate
                      ? "Outline_button"
                      : "Outline_button"
                  }
                >
                  Upload
                  <input
                    type="file"
                    name="certificate_of_good_standing"
                    onChange={(e) => {
                      handleFileUpload(e, "certificate_of_good_standing");
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="py-2 flex justify-between items-center w-full">
                <label className="block medium  light-blue">
                  Certificate of Shareholders (or any other certificate showing
                  shareholders of entity)
                </label>
                <span
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data?.shareholders_certificate ?? ""
                    )
                  }
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                >
                  {getFileName(
                    (shareholders_certificate ??
                      userBusinessInfo?.data?.shareholders_certificate) ||
                      null
                  )}
                </span>
                {(shareholders_certificate ||
                  userBusinessInfo?.data?.shareholders_certificate) && (
                  <div className="mr-4"></div>
                )}
                <label
                  className={
                    isBitramp()
                      ? shareholders_certificate ||
                        userBusinessInfo?.data?.shareholders_certificate
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : shareholders_certificate ||
                        userBusinessInfo?.data?.shareholders_certificate
                      ? "Outline_button"
                      : "Outline_button"
                  }
                >
                  Upload
                  <input
                    type="file"
                    name="certificate_of_shareholders"
                    onChange={(e) => {
                      handleFileUpload(e, "certificate_of_shareholders");
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="py-2 flex justify-between items-center w-full">
                <label className="block medium  light-blue">
                  Certificate of Directors (or any other certificate showing
                  representatives of entity)
                </label>
                <span
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data?.directors_certificate ?? ""
                    )
                  }
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                >
                  {getFileName(
                    (directors_certificate ??
                      userBusinessInfo?.data?.directors_certificate) ||
                      null
                  )}
                </span>
                {(directors_certificate ||
                  userBusinessInfo?.data?.directors_certificate) && (
                  <div className="mr-4"></div>
                )}
                <label
                  className={
                    isBitramp()
                      ? directors_certificate ||
                        userBusinessInfo?.data?.directors_certificate
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : directors_certificate ||
                        userBusinessInfo?.data?.directors_certificate
                      ? "Outline_button"
                      : "Outline_button"
                  }
                >
                  Upload
                  <input
                    type="file"
                    name="certificate_of_directors"
                    onChange={(e) => {
                      handleFileUpload(e, "certificate_of_directors");
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="py-2 flex justify-between items-center w-full">
                <label className="block medium  light-blue">
                  Notice of Registered Office
                </label>
                <span
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data?.notice_of_registered_office ?? ""
                    )
                  }
                >
                  {getFileName(
                    (notice_of_registered_office ??
                      userBusinessInfo?.data?.notice_of_registered_office) ||
                      null
                  )}
                </span>
                {(notice_of_registered_office ||
                  userBusinessInfo?.data?.notice_of_registered_office) && (
                  <div className="mr-4"></div>
                )}
                <label
                  className={
                    isBitramp()
                      ? notice_of_registered_office ||
                        userBusinessInfo?.data?.notice_of_registered_office
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : notice_of_registered_office ||
                        userBusinessInfo?.data?.notice_of_registered_office
                      ? "Outline_button"
                      : "Outline_button"
                  }
                >
                  Upload
                  <input
                    type="file"
                    name="notice_of_registered_office"
                    onChange={(e) => {
                      handleFileUpload(e, "notice_of_registered_office");
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="py-2 flex justify-between items-center w-full">
                <label className="block medium  light-blue">
                  Identification Documents of the Person(s) Managing the Company
                </label>
                <span
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data?.id_of_person_managing_company ??
                        ""
                    )
                  }
                >
                  {getFileName(
                    (id_of_person_managing_company ??
                      userBusinessInfo?.data?.id_of_person_managing_company) ||
                      null
                  )}
                </span>
                {(id_of_person_managing_company ||
                  userBusinessInfo?.data?.id_of_person_managing_company) && (
                  <div className="mr-4"></div>
                )}
                <label
                  className={
                    isBitramp()
                      ? id_of_person_managing_company ||
                        userBusinessInfo?.data?.id_of_person_managing_company
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : id_of_person_managing_company ||
                        userBusinessInfo?.data?.id_of_person_managing_company
                      ? "Outline_button"
                      : "Outline_button"
                  }
                >
                  Upload
                  <input
                    type="file"
                    name="identification_documents"
                    onChange={(e) => {
                      handleFileUpload(e, "identification_documents");
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="py-2 flex justify-between items-center w-full">
                <label className="block medium  light-blue">
                  Resolution Specifying who is Authorised to Act on Behalf of
                  the Company
                </label>
                <span
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data
                        ?.resolution_who_is_authorised_on_behalf ?? ""
                    )
                  }
                >
                  {getFileName(
                    (resolution_who_is_authorised_on_behalf ??
                      userBusinessInfo?.data
                        ?.resolution_who_is_authorised_on_behalf) ||
                      null
                  )}
                </span>
                {(resolution_who_is_authorised_on_behalf ||
                  userBusinessInfo?.data
                    ?.resolution_who_is_authorised_on_behalf) && (
                  <div className="mr-4"></div>
                )}
                <label
                  className={
                    isBitramp()
                      ? resolution_who_is_authorised_on_behalf ||
                        userBusinessInfo?.data
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : resolution_who_is_authorised_on_behalf ||
                        userBusinessInfo?.data
                      ? "Outline_button"
                      : "Outline_button"
                  }
                >
                  Upload
                  <input
                    type="file"
                    name="resolution_specifying"
                    onChange={(e) => {
                      handleFileUpload(e, "resolution_specifying");
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="py-2 flex justify-between items-center w-full">
                <label className="block medium  light-blue">
                  Identification Document(s) of the Person(s) Authorised to Act
                  on Behalf of the Company
                </label>
                <span
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data
                        ?.id_of_who_is_authorised_on_behalf ?? ""
                    )
                  }
                >
                  {getFileName(
                    (id_of_who_is_authorised_on_behalf ??
                      userBusinessInfo?.data
                        ?.id_of_who_is_authorised_on_behalf) ||
                      null
                  )}
                </span>
                {(id_of_who_is_authorised_on_behalf ||
                  userBusinessInfo?.data
                    ?.id_of_who_is_authorised_on_behalf) && (
                  <div className="mr-4"></div>
                )}
                <label
                  className={
                    isBitramp()
                      ? id_of_who_is_authorised_on_behalf ||
                        userBusinessInfo?.data
                          ?.id_of_who_is_authorised_on_behalf
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : id_of_who_is_authorised_on_behalf ||
                        userBusinessInfo?.data
                          ?.id_of_who_is_authorised_on_behalf
                      ? "Outline_button"
                      : "Outline_button"
                  }
                >
                  Upload
                  <input
                    type="file"
                    name="identification_documents_authorised"
                    onChange={(e) => {
                      handleFileUpload(
                        e,
                        "identification_documents_authorised"
                      );
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="flex items-center w-full mt-16">
            <h2
              className={`w-fit whitespace-nowrap semi-bold ${
                isBitramp() ? "text-white" : ""
              }`}
            >
              Individual Person Files
            </h2>
            {isBitramp() ? (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg" />
            ) : (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg" />
            )}
          </div>
          <div className=" w-full">
            <div className="py-2 flex justify-between items-center w-full ps-8">
              <label className="block medium  light-blue">
                Proof of Address
              </label>
              <span
                className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                onClick={() =>
                  handleOpenDocument(
                    userIndividual?.data?.proof_of_address ?? ""
                  )
                }
              >
                {getFileName(
                  (poa ?? userIndividual?.data?.proof_of_address) || null
                )}
              </span>
              {(poa || userIndividual?.data?.proof_of_address) && (
                <div className="mr-4"></div>
              )}
              <label
                className={
                  isBitramp()
                    ? poa || userIndividual?.data?.proof_of_address
                      ? "Upload_button_bitramp"
                      : "Upload_button_bitramp"
                    : poa || userIndividual?.data?.proof_of_address
                    ? "Outline_button"
                    : "Outline_button"
                }
              >
                Upload
                <input
                  type="file"
                  name="poa"
                  onChange={(e) => {
                    handleFileUpload(e, "poa");
                  }}
                  disabled={isDisabled}
                  className="hidden"
                />
              </label>
            </div>
            <div className="py-2 flex justify-between items-center w-full ps-8">
              <label className="block medium  light-blue">
                Proof of Income
              </label>
              <span
                className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                onClick={() =>
                  handleOpenDocument(
                    userIndividual?.data?.proof_of_income ?? ""
                  )
                }
              >
                {getFileName(
                  (poi ?? userIndividual?.data?.proof_of_income) || null
                )}
              </span>
              {(poi || userIndividual?.data?.proof_of_income) && (
                <div className="mr-4"></div>
              )}
              <label
                className={
                  isBitramp()
                    ? poi || userIndividual?.data?.proof_of_income
                      ? "Upload_button_bitramp"
                      : "Upload_button_bitramp"
                    : poi || userIndividual?.data?.proof_of_income
                    ? "Outline_button"
                    : "Outline_button"
                }
              >
                Upload
                <input
                  type="file"
                  name="poi"
                  onChange={(e) => {
                    handleFileUpload(e, "poi");
                  }}
                  disabled={isDisabled}
                  className="hidden"
                />
              </label>
            </div>
            <div className="py-2 flex justify-between items-center w-full ps-8">
              <label className="block medium  light-blue">Passport / ID</label>
              <span
                className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                onClick={() =>
                  handleOpenDocument(userIndividual?.data?.passport_file ?? "")
                }
              >
                {getFileName(
                  (passid ?? userIndividual?.data?.passport_file) || null
                )}
              </span>
              {(passid || userIndividual?.data?.passport_file) && (
                <div className="mr-4"></div>
              )}
              <label
                className={
                  isBitramp()
                    ? passid || userIndividual?.data?.passport_file
                      ? "Upload_button_bitramp"
                      : "Upload_button_bitramp"
                    : passid || userIndividual?.data?.passport_file
                    ? "Outline_button"
                    : "Outline_button"
                }
              >
                Upload
                <input
                  type="file"
                  name="passid"
                  onChange={(e) => {
                    handleFileUpload(e, "passid");
                  }}
                  disabled={isDisabled}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>
        <div className="flex flex-col mt-16">
          <div className="flex justify-between w-full items-center">
            <div
              className={`w-fit whitespace-nowrap semi-bold ${
                isBitramp() ? "text-white" : ""
              }`}
            >
              Bank Account Documents
            </div>
            {isBitramp() ? (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
            ) : (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
            )}
          </div>
          {bankAccounts?.data?.map((bankAccount, index) => {
            const bankAccountStatement = bankAccount.statement ?? null;
            const bankAccountStatementName = getFileName(bankAccountStatement);

            return (
              <div key={index} className="ps-8 flex flex-col py-2">
                <div className="flex w-full justify-between items-center">
                  <div className="block medium  light-blue">
                    Bank Account Statement - <strong>{bankAccount.name}</strong>
                  </div>
                  <div className="flex items-center">
                    <div
                      onClick={() => downloadFile(bankAccountStatement)}
                      className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                    >
                      {bankAccountStatementName}
                    </div>
                    {bankAccountStatement && <div className="mr-4"></div>}
                    <label
                      htmlFor={`fileInput${bankAccount.id}`}
                      className={
                        isBitramp()
                          ? bankAccountStatement
                            ? "Upload_button_bitramp"
                            : "Upload_button_bitramp"
                          : bankAccountStatement
                          ? "Outline_button"
                          : "Outline_button"
                      }
                    >
                      Upload
                      <input
                        type="file"
                        onChange={(e) =>
                          handleUploadBankStatementFile(bankAccount.id, e)
                        }
                        id={`fileInput${bankAccount.id}`}
                        name={`fileInput${bankAccount.id}`}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Cloude;
